import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AgremoService } from '../../core/services/agremo.service';
import { CreateAnalysisDto } from '../../core/models/agremo-analysis.model';
import { MapAnalysisCheckoutService } from '../../core/services/map-analysis-checkout.service';

/**
 * Analysis Checkout Return
 *
 * Stripe-hosted Checkout redirects to this route after the user pays for
 * a Map Analysis. Configured server-side via STRIPE_RETURN_BASE_URL_WEB +
 * the per-flow web default path (billing-payments § 1.0.1). The Hub
 * appends ?session_id={CHECKOUT_SESSION_ID}.
 *
 * Responsibilities (in order):
 *   1. Restore the in-progress analysis DTO from sessionStorage (stashed by
 *      analysis-form.component.payAndSubmit before the redirect).
 *   2. **Verify payment with the Hub** via
 *      `POST /payments/map-analysis/purchase/from-checkout-session` —
 *      Hub re-fetches the Checkout session from Stripe, confirms
 *      payment_status === 'paid', and upserts the audit row. This closes
 *      the "webhook is slow" race (the webhook is the parallel backup
 *      path; whichever lands first wins via idempotency on session id).
 *      See billing-payments § 9.4.
 *   3. Call agremoService.createAnalysis(dto) — passing `purchaseSessionId`
 *      so the Hub atomically links the new analysis_id back to the
 *      purchase row (billing-payments § 9.4.1).
 *   4. Route the user to the analysis detail page on success.
 *
 * Three UI states drive the template:
 *   - `verifying` — between landing here and finalizePurchase resolving
 *   - `processing` — payment confirmed, analysis being created
 *   - `error` / `cancelled` — surface the right copy + recovery path
 *
 * Route: /map-analysis/purchase/return
 */
@Component({
  selector: 'app-analysis-checkout-return',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="container py-5 mw-560px">
      <div class="card shadow-sm">
        <div class="card-body p-4 text-center">
          <ng-container *ngIf="state() === 'verifying'">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Verifying...</span>
            </div>
            <h4 class="mb-2">Verifying payment…</h4>
            <p class="text-muted mb-0">Confirming with Stripe before we kick off your analysis.</p>
          </ng-container>

          <ng-container *ngIf="state() === 'processing'">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Processing...</span>
            </div>
            <h4 class="mb-2">Payment confirmed</h4>
            <p class="text-muted mb-0">Starting your analysis…</p>
          </ng-container>

          <ng-container *ngIf="state() === 'cancelled'">
            <i class="bi bi-x-circle text-warning fs-2-5rem"></i>
            <h4 class="mt-3 mb-2">Checkout cancelled</h4>
            <p class="text-muted mb-3">No payment was charged. You can start over whenever you're ready.</p>
            <a routerLink="/analyses/new" class="btn btn-primary">Back to Analysis Setup</a>
          </ng-container>

          <ng-container *ngIf="state() === 'error'">
            <i class="bi bi-exclamation-triangle text-danger fs-2-5rem"></i>
            <h4 class="mt-3 mb-2">Something went wrong</h4>
            <p class="text-muted mb-2">{{ errorMessage() }}</p>
            <p *ngIf="paymentConfirmed()" class="text-muted small mb-3">
              Your payment was processed — contact support if your analysis doesn't appear shortly.
            </p>
            <a routerLink="/analyses" class="btn btn-outline-primary">Go to Analyses</a>
          </ng-container>
        </div>
      </div>
    </div>
  `,
})
export class AnalysisCheckoutReturnComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private agremoService = inject(AgremoService);
  private checkoutService = inject(MapAnalysisCheckoutService);

  // Initial state is 'verifying' — between landing on this route and the
  // Hub's finalizePurchase call resolving, we don't yet know that Stripe
  // says payment_status === 'paid'. Only after finalize succeeds do we
  // flip to 'processing' (which renders the "Payment confirmed" copy).
  state = signal<'verifying' | 'processing' | 'cancelled' | 'error'>('verifying');
  errorMessage = signal<string>('');
  /**
   * Whether payment was actually confirmed before the error happened. The
   * "contact support if your analysis doesn't appear" footer should only
   * show when this is true — otherwise we'd be falsely implying we have
   * the user's money for a flow that bailed out before any payment.
   */
  paymentConfirmed = signal<boolean>(false);

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;
    if (params.get('cancelled')) {
      this.state.set('cancelled');
      sessionStorage.removeItem('pendingMapAnalysisDto');
      return;
    }

    // Require session_id presence — Stripe (via the Hub's success_url)
    // always appends ?session_id={CHECKOUT_SESSION_ID}. Without it, this
    // route is reachable as a direct URL and would create an analysis for
    // free.
    const sessionId = params.get('session_id');
    if (!sessionId) {
      this.state.set('error');
      this.errorMessage.set(
        'Missing payment session. Open this page only by completing a Stripe checkout from the analysis wizard.',
      );
      return;
    }

    const raw = sessionStorage.getItem('pendingMapAnalysisDto');
    if (!raw) {
      this.state.set('error');
      this.errorMessage.set('Lost track of your analysis setup. Please start over from /analyses.');
      return;
    }

    let dto: CreateAnalysisDto;
    try {
      dto = JSON.parse(raw);
    } catch {
      this.state.set('error');
      this.errorMessage.set('Could not restore your analysis setup. Please start over.');
      sessionStorage.removeItem('pendingMapAnalysisDto');
      return;
    }

    // Foreground-sync the purchase record before creating the analysis.
    // Without this we'd rely solely on the webhook landing — usually fast,
    // but if it's delayed we'd have an analysis with no audit-linked
    // purchase row until the webhook caught up. Idempotent server-side:
    // if the webhook already landed, this call returns the same row.
    // See billing-payments § 9.4 from-checkout-session.
    try {
      await this.checkoutService.finalizePurchase(sessionId);
    } catch (e: any) {
      this.state.set('error');
      // This failure means Stripe says the session isn't paid (status !==
      // 'paid'), or the org/metadata mismatch — NOT a "payment processed
      // but analysis failed" case, so paymentConfirmed stays false.
      this.errorMessage.set(
        e?.error?.error || e?.message || 'Could not verify your payment with Stripe.',
      );
      return;
    }

    // Past this point we have a Hub-verified `payment_status === 'paid'`
    // confirmation. From here on, any error means the analysis couldn't
    // be created *after* the charge succeeded, which is the "contact
    // support" case (footer copy gates on paymentConfirmed).
    this.paymentConfirmed.set(true);
    // Flip the UI from "Verifying payment…" to "Payment confirmed /
    // Starting your analysis…" while createAnalysis runs. Without this
    // transition the 'processing' template branch is unreachable.
    this.state.set('processing');

    // Stamp the funding session onto the create call so the Hub atomically
    // links analysis_id back to the purchase row (billing-payments § 9.3
    // forward-link). Silent on link failure server-side — the analysis is
    // still created; only the audit linkage is lost.
    const dtoWithLink: CreateAnalysisDto = { ...dto, purchaseSessionId: sessionId };

    // Note: AgremoService.createAnalysis() catches HTTP errors itself
    // and returns null — it never throws. So we only branch on the null
    // vs. truthy result here; a try/catch would be dead code.
    const result = await this.agremoService.createAnalysis(dtoWithLink);
    if (result) {
      // ONLY clear the stash on a confirmed successful creation. If
      // we cleared earlier (e.g. unconditionally before this `if`),
      // a transient createAnalysis failure would strand a paid
      // customer with no retry path — refreshing this page would
      // hit the "Lost track of your analysis setup" branch and the
      // only recovery would be re-running checkout (double-charge).
      // Keeping the stash on failure means the user can refresh
      // this URL and the foreground sync + createAnalysis will run
      // again, idempotent on the Hub side via session id.
      sessionStorage.removeItem('pendingMapAnalysisDto');
      this.router.navigate(['/analyses', result.id, 'view']);
    } else {
      this.state.set('error');
      this.errorMessage.set('Payment succeeded but the analysis could not be created. Refresh this page to retry — your payment is safe.');
    }
  }
}
